import React, { useState, useEffect } from 'react';
import { Portal, CircularProgress } from '@mui/material';
import apiClient from '../services/apiClient';
import { useSelector } from 'react-redux';
import { getCurrency } from '../services/tableHelper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface ProductData {
	product_id: number;
	product_name: string;
	total_quantity: number;
	total_revenue: number;
	variants?: VariantData[];
}

interface VariantData {
	variant_id: number;
	variant_name: string;
	quantity_sold: number;
	revenue: number;
}

interface AllProductsModalProps {
	open: boolean;
	onClose: () => void;
	fromDate: string;
	toDate: string;
}

const AllProductsModal: React.FC<AllProductsModalProps> = ({ open, onClose, fromDate, toDate }) => {
	const [viewType, setViewType] = useState<'quantity' | 'revenue'>('quantity');
	const [products, setProducts] = useState<ProductData[]>([]);
	const [expandedProducts, setExpandedProducts] = useState<number[]>([]);
	const [loading, setLoading] = useState(false);
	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	useEffect(() => {
		if (open) {
			fetchAllProducts();
		}
	}, [open, fromDate, toDate]);

	const fetchAllProducts = async () => {
		setLoading(true);
		try {
			const response = await apiClient.get(`/stats/all-products?from=${fromDate}&to=${toDate}`);
			setProducts(response.data || []);
		} catch (error) {
			console.error('Error fetching all products:', error);
			setProducts([]);
		} finally {
			setLoading(false);
		}
	};

	const toggleProductExpansion = (productId: number) => {
		setExpandedProducts((prev) =>
			prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
		);
	};

	const formatValue = (product: ProductData) => {
		if (viewType === 'quantity') {
			return `${product.total_quantity.toLocaleString()} units`;
		} else {
			return `${currency}${Number(product.total_revenue).toFixed(2)}`;
		}
	};

	const formatVariantValue = (variant: VariantData) => {
		if (viewType === 'quantity') {
			return `${variant.quantity_sold.toLocaleString()} units`;
		} else {
			return `${currency}${Number(variant.revenue).toFixed(2)}`;
		}
	};

	const sortedProducts = [...products].sort((a, b) => {
		if (viewType === 'quantity') {
			return b.total_quantity - a.total_quantity;
		} else {
			return b.total_revenue - a.total_revenue;
		}
	});

	if (!open) return null;

	return (
		<Portal>
			<div className="fixed inset-0 z-[9999] overflow-y-auto">
				<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
					{/* Backdrop */}
					<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

					{/* Modal panel */}
					<div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
						{/* Header */}
						<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-gray-900">All Products Sales</h3>
								<div className="flex items-center gap-3">
									<select
										value={viewType}
										onChange={(e) => setViewType(e.target.value as 'quantity' | 'revenue')}
										className="rounded-md border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:ring-blue-500"
									>
										<option value="quantity">By Quantity</option>
										<option value="revenue">By Revenue</option>
									</select>
									<button
										onClick={onClose}
										className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
									>
										<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>

						{/* Content */}
						<div className="px-4 pb-4 sm:px-6 sm:pb-4">
							<div className="max-h-[60vh] overflow-y-auto">
								{loading ? (
									<div className="flex items-center justify-center py-12">
										<CircularProgress size={32} />
									</div>
								) : sortedProducts.length > 0 ? (
									<div className="space-y-1">
										{sortedProducts.map((product, index) => {
											const isExpanded = expandedProducts.includes(product.product_id);
											const hasVariants = product.variants && product.variants.length > 1;

											return (
												<div
													key={product.product_id}
													className="border border-gray-200 rounded-lg overflow-hidden"
												>
													<div
														className={`flex items-center justify-between p-3 ${
															hasVariants ? 'cursor-pointer hover:bg-gray-50' : ''
														} ${isExpanded ? 'bg-gray-50' : 'bg-white'}`}
														onClick={() =>
															hasVariants && toggleProductExpansion(product.product_id)
														}
													>
														<div className="flex items-center flex-1">
															<span className="text-sm text-gray-500 w-8">
																{index + 1}.
															</span>
															<span className="text-sm font-medium text-gray-900">
																{product.product_name}
															</span>
															{hasVariants && (
																<span className="ml-2 text-xs text-gray-500">
																	({product.variants?.length} variants)
																</span>
															)}
														</div>
														<div className="flex items-center">
															<span className="text-sm font-semibold text-gray-900 mr-2">
																{formatValue(product)}
															</span>
															{hasVariants && (
																<div className="text-gray-400">
																	{isExpanded ? (
																		<ExpandLessIcon fontSize="small" />
																	) : (
																		<ExpandMoreIcon fontSize="small" />
																	)}
																</div>
															)}
														</div>
													</div>
													{hasVariants && isExpanded && (
														<div className="bg-gray-50 px-3 pb-3">
															<div className="space-y-1 pt-2">
																{product.variants?.map((variant) => (
																	<div
																		key={variant.variant_id}
																		className="flex items-center justify-between bg-white rounded px-3 py-2"
																	>
																		<span className="text-sm text-gray-600 ml-8">
																			{variant.variant_name}
																		</span>
																		<span className="text-sm font-medium text-gray-700">
																			{formatVariantValue(variant)}
																		</span>
																	</div>
																))}
															</div>
														</div>
													)}
												</div>
											);
										})}
									</div>
								) : (
									<div className="text-center py-12 text-gray-500">
										No products sold in this period
									</div>
								)}
							</div>
						</div>

						{/* Footer */}
						<div className="bg-gray-50 px-4 py-3 sm:px-6">
							<button
								type="button"
								className="w-full sm:w-auto inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
								onClick={onClose}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>
		</Portal>
	);
};

export default AllProductsModal;
